/**
 * Utilitaires pour l'exportation de données
 *
 * Convention pour les exports CSV :
 * - En-têtes = libellés de l'app (i18n, t('resource.key')) pour cohérence avec l'interface.
 * - Ordre des colonnes = même ordre que la table/liste à l'écran.
 * - Passer les en-têtes en 3e argument : exportToCSV(data, filename, headers).
 * - Dates avec heure pour tri Excel : formatDateTimeForExport (YYYY-MM-DD HH:mm).
 */

import { formatDateShort as formatDateShortUtil, formatCurrencyValue, Language } from './formatUtils';

/**
 * Convertit un tableau d'objets en CSV
 * @param headers - Optionnel. Si fourni, définit l'ordre et le libellé des colonnes (recommandé : libellés i18n).
 */
export const exportToCSV = (data: any[], filename: string, headers?: string[]) => {
  if (data.length === 0) {
    return;
  }

  // Use provided headers or keys from first object
  const csvHeaders = headers || Object.keys(data[0]);
  
  // Create header row
  const headerRow = csvHeaders.join(',');
  
  // Create data rows
  const dataRows = data.map(row => 
    csvHeaders.map(header => {
      let value = row[header];
      
      // Convert to string and handle null/undefined
      if (value === null || value === undefined) {
        value = '';
      } else {
        value = String(value);
      }
      
      // Check if we need to escape the value
      const needsEscaping = value.includes(',') || 
                           value.includes('"') || 
                           value.includes('\n') || 
                           value.includes('\r') ||
                           value.includes('\t') ||
                           value.startsWith(' ') ||
                           value.endsWith(' ');
      
      if (needsEscaping) {
        // Replace newlines and carriage returns with spaces to avoid breaking CSV format
        const cleanedValue = value
          .replace(/\r\n/g, ' ') // Windows line breaks
          .replace(/\n/g, ' ')   // Unix line breaks
          .replace(/\r/g, ' ')   // Mac line breaks
          .replace(/\t/g, ' ')   // Tabs
          .trim();               // Remove leading/trailing spaces
        
        // Escape quotes by doubling them and wrap in quotes
        return `"${cleanedValue.replace(/"/g, '""')}"`;
      }
      
      return value;
    }).join(',')
  );
  
  // Combine header and data
  const csvContent = [headerRow, ...dataRows].join('\n');
  
  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Exporte des données au format JSON
 */
export const exportToJSON = (data: any, filename: string) => {
  const jsonContent = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.json`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Formate une date pour l'exportation (date seule, lisible)
 * @param date - La date à formater
 * @param language - La langue à utiliser ('fr' ou 'en'), par défaut 'fr'
 */
export const formatDateForExport = (date: Date | string, language: Language = 'fr'): string => {
  return formatDateShortUtil(date, language);
};

/**
 * Formate une date+heure pour l'export CSV : triable (YYYY-MM-DD HH:mm), même ordre qu'en app.
 * À utiliser pour last_action_at, created_at, updated_at, next_action_at quand l'ordre dans Excel compte.
 */
export const formatDateTimeForExport = (date: Date | string | null | undefined): string => {
  if (date == null) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${day} ${h}:${min}`;
};

/**
 * Formate un montant pour l'exportation
 * @param amount - Le montant à formater
 * @param language - La langue à utiliser ('fr' ou 'en'), par défaut 'fr'
 */
export const formatAmountForExport = (amount: number, language: Language = 'fr'): string => {
  return formatCurrencyValue(amount, language, 'EUR');
};

/**
 * Nettoie une valeur pour l'export CSV en remplaçant les caractères problématiques
 */
export const cleanValueForCSV = (value: string): string => {
  if (!value) return '';
  
  return value
    .replace(/\r\n/g, ' ') // Windows line breaks
    .replace(/\n/g, ' ')   // Unix line breaks  
    .replace(/\r/g, ' ')   // Mac line breaks
    .replace(/\t/g, ' ')   // Tabs
    .trim();               // Remove leading/trailing spaces
};

